import React, { RefObject } from "react";
import classNames from "classnames";
import { BORDER_TYPE } from "@Configs/enum";
import { Tooltip } from "antd";
import "./TextArea.scss";
import { TFunction } from "i18next";
import CircleExclamation from "assets/icons/CircleExclamation";

export interface TextAreaAction {
  name?: string;
  action?: never;
}

export interface TextAreaProps {
  /**Label for current field*/
  label?: string;
  /**Control the style type of component: MATERIAL, BORDERED, FLOAT_LABEL */
  type?: BORDER_TYPE;
  /**Show symbol * as required field*/
  isRequired?: boolean;
  /**User-filled value*/
  value?: string;
  /**Not allow to handle change value*/
  disabled?: boolean;
  /**Placeholder of the component*/
  placeHolder?: string;
  /**Use to custom style the component*/
  className?: string;
  /**Boolean to show the lenght of value user-filled*/
  showCount?: boolean;
  /**Set maximum length of value*/
  maxLength?: number;
  /**Provide a custom action (onClick) to the component*/
  action?: TextAreaAction;
  /** Custom background color for component: "white" || "gray" */
  bgColor?: "white" | "gray";
  /**Handle the change value of the field*/
  onChange?: (T: string | null) => void;
  /**Handle onEnter action*/
  onEnter?: (T: string | null) => void;
  /**Handle onBlur action*/
  onBlur?: (T: string | null) => void;
  rows?: number;
  cols?: number;
  /**pass translate to explain the description of errors */
  translate?: TFunction;
  /**Pass resize to set type of resize or disable resize */
  resize?: "none" | "both" | "horizontal" | "vertical" | "block" | "inline";
  /**Pass true to only show error in tooltip */
  isTableCell?: boolean;
  /**Read only field*/
  readOnly?: boolean;
}

function TextArea(props: TextAreaProps) {
  const {
    label = "",
    type = BORDER_TYPE.BORDERED,
    isRequired = false,
    disabled = false,
    className = "",
    maxLength = 2000,
    action,
    showCount = false,
    value,
    placeHolder,
    bgColor = "white",
    onChange,
    onEnter,
    onBlur,
    rows = 3,
    cols,
    translate,
    resize = "vertical",
    isTableCell = false,
    readOnly = false,
  } = props;

  const [internalValue, setInternalValue] = React.useState<string>("");

  const [messageValidation, setMessageValidation] =
    React.useState<string>(null);

  const inputRef: RefObject<HTMLTextAreaElement> =
    React.useRef<HTMLTextAreaElement>(null);

  const handleChange = React.useCallback(
    (event: React.ChangeEvent<HTMLTextAreaElement>) => {
      setInternalValue(event.target.value);
      if (typeof onChange === "function") {
        onChange(event.currentTarget.value);
      }
      const value = event.target.value;
      if (
        value?.length > maxLength &&
        translate
      ) {
        setMessageValidation(
          `${translate("CM.input_length_validation", { maxLength })}`
        );
      } else if (isRequired && !value?.length && translate) {
        setMessageValidation(`${translate("CM.input_require_validation")}`);
      } else {
        setMessageValidation(null);
      }
    },
    [onChange, maxLength]
  );

  const handleKeyPress = React.useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === "Enter") {
        if (typeof onEnter === "function") {
          onEnter(event.currentTarget.value);
        }
      }
    },
    [onEnter]
  );
  const handleBlur = React.useCallback(
    (event: React.FocusEvent<HTMLTextAreaElement>) => {
      const value = event.target.value;
      if (typeof onBlur === "function") {
        onBlur(value);
      }
    },
    [onBlur]
  );

  React.useEffect(() => {
    if (value) {
      setInternalValue(value);
    } else {
      setInternalValue("");
    }
  }, [value]);

  return (
    <>
      <div className={classNames("text-area__wrapper", className)}>
        {label && (
          <div className="text-area__label m-b--3xs p-l--3xs">
            {type !== BORDER_TYPE.FLOAT_LABEL && label && (
              <label className="component__title">
                {label}
                {isRequired && <span className="text-danger">&nbsp;*</span>}
              </label>
            )}
            <div className="label__right">
              {/* {showCount && maxLength > 0 && (
              <span className="text-area__count p-l--xs body-text--xs">
                {internalValue.length}/{maxLength}
              </span>
            )} */}
              {action && (
                <span
                  className="m-l--3xs body-text--md color-link"
                  style={{ cursor: "pointer" }}
                  onClick={action.action}
                >
                  {action.name}
                </span>
              )}
            </div>
          </div>
        )}
        <div
          className={classNames(
            "component__input text-area__container p-l--2xs p-b--3xs p-t--3xs",
            {
              "text-area--white": bgColor === "white",
              "text-area--disabled ": disabled,
              "text-area--material": type === BORDER_TYPE.MATERIAL,
              "text-area--bordered": type === BORDER_TYPE.BORDERED,
              "text-area--float": type === BORDER_TYPE.FLOAT_LABEL,
              "text-area--readOnly": readOnly,
            }
          )}
          onClick={() => {
            inputRef.current.focus();
          }}
        >
          <Tooltip
            color={isTableCell && messageValidation ? "red" : "black"}
            overlayClassName="input-error__tooltip"
            title={
              isTableCell && messageValidation ? messageValidation : undefined
            }
            placement="topLeft"
          >
            <textarea
              value={internalValue}
              onChange={handleChange}
              onKeyDown={() => handleKeyPress}
              onBlur={handleBlur}
              placeholder={
                type === BORDER_TYPE.FLOAT_LABEL && label ? " " : placeHolder
              }
              ref={inputRef}
              disabled={disabled}
              className={classNames("component__input", {
                "disabled-field": disabled,
              })}
              rows={rows}
              cols={cols}
              style={{ resize: resize }}
              readOnly={readOnly}
            ></textarea>
          </Tooltip>
          {showCount && maxLength > 0 && (
            <span className="text-area__count p-l--xs p-t--3xs body-text--xs">
              {internalValue?.length}/{maxLength}
            </span>
          )}
          {type === BORDER_TYPE.FLOAT_LABEL && label && (
            <label className="component__title">
              {label}
              {isRequired && <span className="text-danger">&nbsp;*</span>}
            </label>
          )}
        </div>
        {messageValidation ? (
          <div
            className={classNames("message-validation", {
              "is-table-cell": isTableCell,
            })}
          >
            <CircleExclamation />
            <span className="m-l--3xs">{messageValidation}</span>
          </div>
        ) : (
          <></>
        )}
      </div>
    </>
  );
}

export default TextArea;
